/**
 * Beat 1 of Decide, "Call the year" (screens §P6, content §2.4; OD5-1, OD5-6): the no-change plan's NIM, NCO rate and marked
 * CET1, committed once per team per round. Executive rounds type three percentages; guided rounds render the band picker slot.
 * While the call is open this is the navy sheet; once committed it is the call strip.
 */
import { useEffect, useState } from "react";
import type { ErrorBody, ForecastInput, ForecastStatus } from "@bsim/contracts";
import { GxErrorBanner } from "../../theme/primitives";
import { Icon } from "../../theme/icons";
import { clockTime, fiscalYearLabel, formatKpi } from "../../format";
import { useRoute } from "../../api/context";
import type { CallTheYearProps } from "../slotTypes";
import { FORECAST_FIELDS, committerName, lastYearTexts, numericForecastBody, parsePercent, standingBuybackLine, usePersistedCall } from "./forecast";
import { GxDescribed } from "../../theme/GxTerm";
import "./forecast.css";
import { FORECAST_GRADED_ON_STEP } from "../../format/basis";
import { BP_LINE, firstUseDefinition, labelOf } from "../../format/labels";
import { bandLabel, CALL_NAME } from "../guided/guided";
import { GxKpiLabel } from "../../theme/GxTerm";


/** `bandPicker` is `decideSlots.bandPicker`, handed down by Decide: importing the registry here would be a cycle. */
export function CallTheYear({ team, catalog, commitForecast, onStatusChange, onDismiss, bandPicker: BandPicker }: CallTheYearProps) {
  const [committed, setCommitted] = useState<ForecastStatus | null>(null);
  // The fields open at the figure the call is scored against: last year's average on the team's own bank, from the forecast status.
  const opening = lastYearTexts(team.forecast);
  const [text, setText] = usePersistedCall(`bsim.call.${team.team.id}.${team.round?.id ?? ""}`, opening);
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<ErrorBody["error"] | null>(null);
  const [conflict, setConflict] = useState(false);

  // A 409 means a teammate called first: reload the team's view and show their call, read-only.
  const reload = useRoute("teamState", conflict ? { teamId: team.team.id } : null);
  const reloaded = reload.data?.forecast ?? null;
  useEffect(() => {
    if (reloaded?.state === "committed") { setCommitted(reloaded); onStatusChange(reloaded); }
  }, [reloaded, onStatusChange]);

  const status = committed ?? team.forecast;
  if (status === null) return null;
  const roundId = team.round?.id;

  async function commit(body: ForecastInput) {
    if (roundId === undefined) return;
    setPending(true);
    const result = await commitForecast({ teamId: team.team.id, roundId }, body);
    setPending(false);
    if (result.ok) { setFailure(null); setCommitted(result.value); onStatusChange(result.value); }
    else if (result.error.code === "FORECAST_ALREADY_COMMITTED") setConflict(true);
    else setFailure(result.error);
  }

  if (status.state === "committed") {
    return <CallStrip status={status} name={committerName(team, status)} conflict={conflict} solo={team.cohort.mode === "solo"} />;
  }

  const roundNo = team.round?.roundNo ?? 0;
  const values = FORECAST_FIELDS.map((f) => parsePercent(text[f.kpi]));
  const [nim, ncoRate, cet1Marked] = values;
  const ready = nim !== null && nim !== undefined && ncoRate !== null && ncoRate !== undefined && cet1Marked !== null && cet1Marked !== undefined;
  const solo = team.cohort.mode === "solo";
  const missing = FORECAST_FIELDS.filter((_, i) => values[i] === null);

  return (
    <section className="gx-call" data-reveal data-testid="forecast.sheet" aria-labelledby="gx-call-h">
      <h2 className="gx-call-h" id="gx-call-h">Where does {fiscalYearLabel(catalog, roundNo)} end if you change nothing?</h2>
      <p className="gx-call-body">Forecast your margin, loan losses and Marked CET1 for the year if you change nothing: <GxDescribed label="The no-change plan" plain={`Your bank as it starts this year: ${standingBuybackLine(team, catalog)}, pauses buybacks if the outlook turns (as your standing policy says), and changes nothing else. This year\u2019s decisions cannot move it. It shows in your debrief.`}>the no-change plan</GxDescribed>. {solo ? "One forecast per round." : "One forecast per team per round."}</p>
      <p className="gx-call-body" data-testid="forecast.gradedon">{FORECAST_GRADED_ON_STEP}</p>
      <p className="gx-call-body gx-muted" data-testid="forecast.cet1def">{labelOf("cet1Marked")}: {firstUseDefinition("cet1Marked")}</p>
      {status.format === "band" ? <p className="gx-call-body" data-testid="forecast.bp">{BP_LINE}</p> : null}
      {status.format === "band" ? (
        BandPicker === undefined
          ? <p className="gx-call-body" data-testid="forecast.bandUnavailable">Band picker unavailable</p>
          : <BandPicker status={status} pending={pending} solo={solo} onCommit={(body) => void commit(body)} />
      ) : (
        <>
          <div className="gx-call-fields">
            {FORECAST_FIELDS.map((f) => (
              <label key={f.kpi} className="gx-call-field">
                <span className="gx-call-label">{f.label}</span>
                <span className={`gx-call-input${text[f.kpi] === opening[f.kpi] && opening[f.kpi] !== "" ? " gx-call-input--prefilled" : ""}`}>
                  <input className="gx-num" inputMode="decimal" autoComplete="off" data-testid={`forecast.input.${f.kpi}`}
                    value={text[f.kpi]} onChange={(e) => setText({ ...text, [f.kpi]: e.target.value })} />
                  <span aria-hidden="true">%</span>
                </span>
              </label>
            ))}
          </div>
          <p className="gx-call-last" data-testid="forecast.basis">Each figure opens at last year&apos;s (the margin and loans written off as the year&apos;s average, Marked CET1 at year-end), dimmed until you change it: change it where you expect a move.</p>
          <div className="gx-call-foot">
            <span className="gx-call-need">{missing.length === 0 ? "" : `${missing.map((f) => f.label).join(", ")} still needed`}</span>
            <button type="button" className="gx-btn gx-btn--accent" data-testid="forecast.commit" disabled={!ready || pending || roundId === undefined}
              onClick={() => { if (ready) void commit(numericForecastBody(nim, ncoRate, cet1Marked)); }}>{pending ? "Committing\u2026" : solo ? "Commit my forecast" : "Commit our forecast"}</button>
            <button type="button" className="gx-btn gx-btn--onnavy" data-testid="forecast.dismiss" onClick={onDismiss}>Look at the decisions first</button>
          </div>
        </>
      )}
      {failure === null ? null : <GxErrorBanner error={failure} />}
      {status.format === "band" ? <button type="button" className="gx-btn gx-btn--onnavy gx-call-dismiss" data-testid="forecast.dismiss" onClick={onDismiss}>Look at the decisions first</button> : null}
    </section>
  );
}

/** The committed call, as one line in the page header (DESIGN §5.2): "Your forecast: Margin (NIM) 3.60% · Loan losses (NCO rate) 0.30% · Marked CET1 9.20%". Read-only; a 409 adds the "already made the forecast" line. */
function CallStrip({ status, name, conflict }: { readonly status: ForecastStatus; readonly name: string | null; readonly conflict: boolean; readonly solo: boolean }) {
  const time = status.committedAt === null ? "" : clockTime(status.committedAt);
  const call = status.forecast;
  return (
    <div className="gx-call-strip" data-testid="forecast.strip" role="status" title={time === "" ? undefined : `Forecast saved at ${time}`}>
      <Icon name="check" size={14} />
      <span className="gx-call-strip-h">Your forecast:</span>
      {FORECAST_FIELDS.map((f, i) => (
        <span key={f.kpi} className="gx-num">{i === 0 ? "" : <span className="gx-call-sep" aria-hidden="true">{"\u00b7 "}</span>}<GxKpiLabel kpi={f.kpi}>{CALL_NAME[f.kpi]}</GxKpiLabel> <b data-testid={`forecast.value.${f.kpi}`}>{call === null ? "" : call.format === "numeric" ? formatKpi(f.kpi, call[f.kpi]) : bandLabel(call[f.kpi], status.bands?.[f.kpi])}</b></span>
      ))}
      {conflict ? <span data-testid="forecast.conflict">{name ?? "A teammate"} already made the forecast{time === "" ? "" : ` at ${time}`}.</span> : null}
    </div>
  );
}
